import { Popover, Transition } from "@headlessui/react";
import clsx from "clsx";
import dayjs from "dayjs";
import { useMemo, useState } from "react";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";

import { useTimezone } from "@web-app/api";
import { TIMEZONE_MAP, timeZoneMap } from "@web-app/utils/helpers";

import Button from "../Button";
import { Icon } from "../Icon";
import Input from "../Input";
import SelectInput from "../SelectInput";

// Add styles for react-day-picker
const calendarStyles = `
  .rdp {
    margin: 0;
    --rdp-cell-size: 32px;
    --rdp-accent-color: #1f2937;
    --rdp-background-color: #f3f4f6;
    --rdp-accent-color-dark: #111827;
    --rdp-background-color-dark: #374151;
  }
  .rdp-day_selected {
    background-color: var(--rdp-accent-color);
    color: white;
  }
  .rdp-day:hover:not([disabled]):not(.rdp-day_selected) {
    background-color: var(--rdp-background-color);
  }
`;

type DateInputPropTypes = {
  disabled?: boolean;
  invalid?: boolean;
  maxDate?: Date;
  minDate?: Date;
  name?: string;
  onChange?: (value: Date) => void;
  position?: "over" | "under";
  /**
   * @deprecated - readOnly is not supported anymore. Render normally or used disabled prop instead.
   */
  readOnly?: boolean;
  required?: boolean;
  timezone?: keyof typeof timeZoneMap;
  value: Date;
};

function DateInput({ ...props }: DateInputPropTypes) {
  const { getTimezone, getTimezoneValue } = useTimezone();

  // Get the effective timezone (order's timezone, user's timezone, or system)
  const effectiveTimezone = getTimezone(props.timezone);

  // Convert UTC value to the effective timezone for display
  const getDateInTimezone = (date: Date) => {
    if (effectiveTimezone === "system") {
      return dayjs(date);
    }
    return dayjs.utc(date).tz(effectiveTimezone);
  };

  const dateInTimezone = getDateInTimezone(props.value);

  // For DayPicker, we need to create a Date that will display correctly in local time
  // We extract year/month/day from the timezone-adjusted date and create a new local Date
  const getLocalDateForPicker = () => {
    const year = dateInTimezone.year();
    const month = dateInTimezone.month();
    const day = dateInTimezone.date();
    return new Date(year, month, day);
  };

  const [dateTime, setDateTime] = useState(getLocalDateForPicker());
  const [hours, setHours] = useState(dateInTimezone.format("hh"));
  const [minutes, setMinutes] = useState(dateInTimezone.format("mm"));
  const [meridiem, setMeridiem] = useState(dateInTimezone.format("A"));

  // Extract seconds from the original value (not editable in UI)
  const seconds = dateInTimezone.format("ss");

  const getMinDate = (minDate: Date | undefined) => {
    if (!minDate) return undefined;
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    yesterday.setHours(23, 59, 59);
    return yesterday;
  };

  const minDate = getMinDate(props.minDate);
  const convertTimeAccordingToTimezone = () => {
    const orderTimezone = getTimezone(props.timezone);

    if (orderTimezone === "system") {
      return dayjs(props.value).format("MM/DD/YYYY hh:mm A");
    }

    return dayjs
      .utc(props.value)
      .tz(orderTimezone)
      .format("MM/DD/YYYY hh:mm A");
  };

  const onChange = () => {
    // Convert hours to 24-hour format
    const hour24 =
      meridiem === "AM"
        ? parseInt(hours) === 12
          ? 0
          : parseInt(hours)
        : parseInt(hours) === 12
          ? 12
          : parseInt(hours) + 12;

    // Get the date components from the selected dateTime
    // Use the date as displayed in the picker (year, month, day)
    const year = dateTime.getFullYear();
    const month = String(dateTime.getMonth() + 1).padStart(2, "0");
    const day = String(dateTime.getDate()).padStart(2, "0");
    const dateStr = `${year}-${month}-${day} ${String(hour24).padStart(2, "0")}:${minutes}:${seconds}`;

    let resultDate: Date;
    if (effectiveTimezone === "system") {
      // If system timezone, create date directly (local time)
      resultDate = new Date(dateStr.replace(" ", "T"));
    } else {
      // Convert from order's timezone to UTC
      const timezoneToUse =
        TIMEZONE_MAP[effectiveTimezone] || effectiveTimezone;
      resultDate = dayjs.tz(dateStr, timezoneToUse).utc().toDate();
    }

    props.onChange?.(resultDate);
  };

  const isDisabledSubmit = useMemo(() => {
    return (
      !hours ||
      !minutes ||
      parseInt(hours) < 1 ||
      parseInt(hours) > 12 ||
      parseInt(minutes) < 0 ||
      parseInt(minutes) > 59
    );
  }, [hours, minutes]);

  if (props.readOnly) {
    return (
      <div className="relative w-full" data-testid={`date-input-${props.name}`}>
        <span className="tw-form-field-readonly-style">
          {dayjs
            .utc(props.value)
            .tz(props.timezone)
            .format("MM/DD/YYYY hh:mm A")}
        </span>
      </div>
    );
  }

  return (
    <div className="relative w-full" data-testid={`date-input-${props.name}`}>
      <style>{calendarStyles}</style>
      {props.readOnly ? (
        <span className="tw-form-field-readonly-style">
          {dayjs(props.value).format("MM/DD/YYYY hh:mm A")}
        </span>
      ) : (
        <Popover>
          <Popover.Button
            className={clsx(
              "block w-full rounded-md border px-3 py-2 text-gray-900 shadow-sm text-xs lg:text-sm",
              props.invalid
                ? "border-red-300 focus:border-red-500 focus:ring-red-500"
                : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
              "focus:outline-none focus:ring-2 focus:ring-opacity-50",
              "disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500",
              "pl-8 lg:pl-10",
              "flex items-center justify-start",
            )}
            disabled={props.disabled}
            name="button"
          >
            <div className="pointer-events-none absolute left-3 flex h-full items-center">
              <Icon
                name="fasCalendarDays"
                className="h-3 lg:h-4 w-3 lg:w-4 text-gray-400"
                aria-hidden="true"
              />
            </div>

            {<span>{convertTimeAccordingToTimezone()}</span>}

            {props.timezone && (
              <span className="ml-1 text-gray-900">
                {getTimezoneValue(props.timezone)}
              </span>
            )}
          </Popover.Button>
          <Transition
            enter="transition duration-100 ease-out"
            enterFrom="transform scale-95 opacity-0"
            enterTo="transform scale-100 opacity-100"
            leave="transition duration-75 ease-out"
            leaveFrom="transform scale-100 opacity-100"
            leaveTo="transform scale-95 opacity-0"
            className="relative z-[60] shadow-md"
            as="div"
          >
            <Popover.Panel
              className={clsx(
                "absolute z-10  h-full w-full bg-white",
                props.position === "under"
                  ? "top-0"
                  : "top-[-385px] lg:top-[-415px] left-0 lg:left-[-15px]",
              )}
            >
              {({ close }) => (
                <div className="flex w-[280px] flex-col rounded-md border bg-white shadow-md">
                  <DayPicker
                    mode="single"
                    selected={dateTime}
                    onSelect={(date) => date && setDateTime(date)}
                    disabled={[
                      ...(minDate ? [{ before: minDate }] : []),
                      ...(props.maxDate ? [{ after: props.maxDate }] : []),
                    ]}
                  />
                  <div className="flex gap-2 mt-3 p-2">
                    <div className="flex-1">
                      <Input
                        type="number"
                        placeholder="hh"
                        min={0}
                        max={11}
                        value={hours}
                        onChange={(value) => setHours(value)}
                        name="hours"
                      />
                    </div>
                    <div className="flex-1">
                      <Input
                        type="number"
                        min={0}
                        max={59}
                        placeholder="mm"
                        value={minutes}
                        onChange={(value) => setMinutes(value)}
                        name="minutes"
                      />
                    </div>
                    <div className="flex-1">
                      <SelectInput
                        options={[
                          {
                            disabled: false,
                            label: "AM",
                            value: "AM",
                          },
                          {
                            disabled: false,
                            label: "PM",
                            value: "PM",
                          },
                        ]}
                        selected={meridiem}
                        required
                        onChange={(selected) => setMeridiem(selected)}
                      />
                    </div>
                  </div>
                  <div className="items-right mt-2 flex flex-row-reverse border-t p-2">
                    <Button
                      type="button"
                      onClick={() => {
                        onChange();
                        close();
                      }}
                      disabled={isDisabledSubmit}
                    >
                      Done
                    </Button>
                  </div>
                </div>
              )}
            </Popover.Panel>
          </Transition>
        </Popover>
      )}
    </div>
  );
}

export default DateInput;
